import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  CloseOutlined as CloseIcon,
  PhotoCameraOutlined as PhotoCameraIcon,
  PhotoLibraryOutlined as PhotoLibraryIcon,
  UploadFileOutlined as UploadFileIcon
} from '@mui/icons-material';
import {
  confirmLeaseIntake,
  discardLeaseIntake,
  getLeaseIntake,
  MAX_LEASE_FILE_BYTES,
  readLeaseIntake,
  uploadLeaseIntake
} from './api';
import {
  initialLeaseForm,
  leaseFormProblems,
  readingStatus,
  STATUS_TEXT,
  tenantDisplayName,
  unitDisplayName
} from './leaseReading';

const checkSx = { color: 'warning.dark', fontWeight: 600 };

const FIELD_SOURCES = {
  unitId: 'unitLabel',
  tenantId: 'tenantNames',
  startDate: 'startDate',
  endDate: 'endDate',
  monthlyRent: 'rentAmount',
  securityDeposit: 'securityDeposit',
  lateFee: 'lateFee'
};

function apiError(err, fallback) {
  if (err?.response?.status === 404) return 'Adding leases from a signed copy is not turned on for this account yet.';
  return err?.response?.data?.error || fallback;
}

function formatSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}\u00a0KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}\u00a0MB`;
}

function readingValue(extraction, name) {
  const f = extraction?.fields?.[name];
  return f && f.value != null ? f.value : null;
}

/**
 * Signed lease → lease record: choose the property, add the pages, check what was read, create the lease.
 * Nothing becomes a lease until "Create lease"; closing keeps the upload for later.
 */
function LeaseIntakeDialog({
  open,
  resumeIntakeId,
  properties,
  tenants,
  aiEnabled,
  maxFiles = 10,
  onClose,
  onCreated,
  onChanged
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [phase, setPhase] = useState('choose');
  const [propertyId, setPropertyId] = useState('');
  const [pages, setPages] = useState([]);
  const [sourceType, setSourceType] = useState('file_upload');
  const [progress, setProgress] = useState(0);
  const [intake, setIntake] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const cameraInput = useRef(null);
  const libraryInput = useRef(null);
  const pdfInput = useRef(null);

  const property = properties.find((p) => p.id === (intake?.propertyId || propertyId)) || null;
  const units = Array.isArray(property?.units) ? property.units.filter((u) => u && u.id) : [];

  const showReview = (loaded) => {
    setIntake(loaded);
    setForm(initialLeaseForm(loaded.extraction, properties.find((p) => p.id === loaded.propertyId), tenants));
    setPhase('review');
  };

  const readAndReview = async (id) => {
    setPhase('reading');
    setError(null);
    try {
      await readLeaseIntake(id);
    } catch (err) {
      setError(apiError(err, 'We could not read the lease just now. Type the details in, or try reading again.'));
    }
    try {
      showReview(await getLeaseIntake(id));
      onChanged?.();
    } catch (err) {
      setError(apiError(err, 'Could not open this lease upload.'));
      setPhase('choose');
    }
  };

  const openExisting = async (id) => {
    setPhase('loading');
    setError(null);
    setDuplicate(null);
    try {
      const loaded = await getLeaseIntake(id);
      if (loaded.status === 'uploaded') await readAndReview(id);
      else showReview(loaded);
    } catch (err) {
      setError(apiError(err, 'Could not open this lease upload.'));
      setPhase('choose');
    }
  };

  useEffect(() => {
    if (!open) return;
    setPhase('choose');
    setPropertyId('');
    setPages([]);
    setIntake(null);
    setForm(null);
    setError(null);
    setDuplicate(null);
    setConfirmingDiscard(false);
    if (resumeIntakeId) openExisting(resumeIntakeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resumeIntakeId]);

  const addFiles = (event, kind) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (!picked.length) return;
    setError(null);
    if (picked.some((f) => f.size > MAX_LEASE_FILE_BYTES)) {
      setError('Each file must be 15 MB or smaller.');
      return;
    }
    if (kind === 'file_upload') {
      setPages(picked.slice(0, 1));
    } else {
      const photos = pages.filter((p) => p.type !== 'application/pdf');
      const next = [...photos, ...picked];
      if (next.length > maxFiles) {
        setError(`Add up to ${maxFiles} page photos.`);
        return;
      }
      setPages(next);
    }
    setSourceType(kind);
  };

  const upload = async () => {
    setError(null);
    setDuplicate(null);
    setProgress(0);
    setPhase('uploading');
    try {
      const created = await uploadLeaseIntake(pages, propertyId, sourceType, setProgress);
      onChanged?.();
      await readAndReview(created.id);
    } catch (err) {
      const existing = err?.response?.status === 409 ? err.response.data?.intake : null;
      if (existing) setDuplicate(existing);
      setError(apiError(err, 'Upload failed. Please try again.'));
      setPhase('choose');
    }
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const problems = form ? leaseFormProblems(form) : [];

  const createLease = async () => {
    if (problems.length) return;
    setBusy(true);
    setError(null);
    try {
      const tenant = tenants.find((t) => t.id === form.tenantId);
      const result = await confirmLeaseIntake(intake.id, {
        propertyId: intake.propertyId,
        unitId: form.unitId,
        tenantId: form.tenantId,
        tenantName: tenant ? tenantDisplayName(tenant) : form.tenantName,
        startDate: form.startDate,
        endDate: form.endDate,
        monthlyRent: form.monthlyRent,
        securityDeposit: form.securityDeposit,
        lateFee: form.lateFee,
        status: form.status,
        notes: form.notes
      });
      onChanged?.();
      onCreated?.(result.lease);
    } catch (err) {
      const details = err?.response?.data?.details;
      setError(
        `${apiError(err, 'Could not create the lease.')}${Array.isArray(details) ? ` ${details.join('; ')}` : ''}`
      );
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    setError(null);
    try {
      await discardLeaseIntake(intake.id);
      onChanged?.();
      onClose();
    } catch (err) {
      setError(apiError(err, 'Could not discard this upload.'));
    } finally {
      setBusy(false);
      setConfirmingDiscard(false);
    }
  };

  const helper = (field) => {
    const status = readingStatus(intake?.extraction, FIELD_SOURCES[field]);
    return status ? STATUS_TEXT[status] : undefined;
  };
  const needsCheck = (field) => readingStatus(intake?.extraction, FIELD_SOURCES[field]) === 'check';

  const extraction = intake?.extraction;
  const warnings = extraction?.warnings || [];
  const tenantNames = readingValue(extraction, 'tenantNames') || [];
  const frequency = readingValue(extraction, 'rentFrequency');
  const leaseRent = readingValue(extraction, 'rentAmount');
  const readFailed = intake?.status === 'read_failed';
  const nothingRead = extraction?.provider === 'manual';

  const renderChoose = () => (
    <Stack spacing={2}>
      <Typography variant="body1" color="text.secondary">
        Take a photo of each page or upload the PDF.{' '}
        {aiEnabled
          ? 'We read the names, dates and rent for you to check.'
          : 'You type the details in next to the pages.'}{' '}
        Nothing becomes a lease until you confirm it.
      </Typography>
      <FormControl fullWidth required>
        <InputLabel id="lease-intake-property">Property</InputLabel>
        <Select
          labelId="lease-intake-property"
          label="Property"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
        >
          {properties.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name || p.address || 'Property'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Button variant="outlined" startIcon={<PhotoCameraIcon />} onClick={() => cameraInput.current?.click()}>
          {pages.length && sourceType !== 'file_upload' ? 'Take next page' : 'Take photos'}
        </Button>
        <Button variant="outlined" startIcon={<PhotoLibraryIcon />} onClick={() => libraryInput.current?.click()}>
          Choose photos
        </Button>
        <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => pdfInput.current?.click()}>
          Upload PDF
        </Button>
      </Stack>
      <input
        ref={cameraInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        hidden
        data-testid="lease-camera-input"
        onChange={(e) => addFiles(e, 'camera')}
      />
      <input
        ref={libraryInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        data-testid="lease-library-input"
        onChange={(e) => addFiles(e, 'photo_library')}
      />
      <input
        ref={pdfInput}
        type="file"
        accept="application/pdf"
        hidden
        data-testid="lease-pdf-input"
        onChange={(e) => addFiles(e, 'file_upload')}
      />
      <Typography variant="caption" color="text.secondary">
        One PDF, or up to {maxFiles} photos (JPEG, PNG or WebP) in page order, 15 MB each.
      </Typography>
      {pages.length > 0 && (
        <List dense disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          {pages.map((file, index) => (
            <ListItem
              key={`${file.name}-${index}`}
              divider={index < pages.length - 1}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label={`Remove page ${index + 1}`}
                  onClick={() => setPages((prev) => prev.filter((_, i) => i !== index))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={file.type === 'application/pdf' ? file.name || 'Lease PDF' : `Page ${index + 1}`}
                secondary={`${file.name ? `${file.name} · ` : ''}${formatSize(file.size)}`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Stack>
  );

  const renderPages = () => {
    const previews = intake?.previews || [];
    if (!previews.length) return null;
    return (
      <Box sx={{ maxHeight: { md: '65vh' }, overflowY: 'auto', pr: { md: 1 } }}>
        <Stack spacing={1.5}>
          {previews.map((page, index) =>
            page.mimeType === 'application/pdf' ? (
              <Box key={page.previewUrl}>
                <Box
                  component="iframe"
                  src={page.previewUrl}
                  title="Signed lease"
                  sx={{ display: { xs: 'none', md: 'block' }, width: '100%', height: '60vh', border: 0, borderRadius: 2 }}
                />
                <Link href={page.previewUrl} target="_blank" rel="noopener noreferrer">
                  Open the signed lease (PDF)
                </Link>
              </Box>
            ) : (
              <Link key={page.previewUrl} href={page.previewUrl} target="_blank" rel="noopener noreferrer">
                <Box
                  component="img"
                  src={page.previewUrl}
                  alt={`Lease page ${index + 1}`}
                  sx={{ width: '100%', display: 'block', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                />
              </Link>
            )
          )}
        </Stack>
      </Box>
    );
  };

  const moneyField = (field, label) => (
    <TextField
      label={label}
      value={form[field]}
      onChange={(e) => setField(field, e.target.value)}
      type="number"
      fullWidth
      required={field === 'monthlyRent'}
      helperText={helper(field)}
      FormHelperTextProps={{ sx: needsCheck(field) ? checkSx : undefined }}
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
      inputProps={{ min: 0, step: '0.01' }}
    />
  );

  const renderReview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        {renderPages()}
      </Grid>
      <Grid item xs={12} md={7}>
        <Stack spacing={2}>
          {readFailed && (
            <Alert severity="warning">We could not read this lease. Type the details in from the pages.</Alert>
          )}
          {nothingRead && <Alert severity="info">Type the lease details in from the pages.</Alert>}
          {warnings.includes('not_a_lease') && (
            <Alert severity="warning">This may not be a residential lease. Check the pages before you continue.</Alert>
          )}
          {frequency && frequency !== 'monthly' && leaseRent != null && (
            <Alert severity="info">
              The lease sets rent ${leaseRent} {frequency === 'weekly' ? 'every week' : frequency === 'biweekly' ? 'every two weeks' : 'on another schedule'}. Enter the monthly amount.
            </Alert>
          )}
          {(tenantNames.length > 0 || readingValue(extraction, 'propertyAddress')) && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5 }}>
              {readingValue(extraction, 'propertyAddress') && (
                <Typography variant="body2">
                  <strong>Address on the lease:</strong> {readingValue(extraction, 'propertyAddress')}
                  {readingValue(extraction, 'unitLabel') ? `, ${readingValue(extraction, 'unitLabel')}` : ''}
                </Typography>
              )}
              {tenantNames.length > 0 && (
                <Typography variant="body2">
                  <strong>Tenants on the lease:</strong> {tenantNames.join(', ')}
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            Property: <strong>{property ? property.name || property.address : 'Property'}</strong>
          </Typography>
          <FormControl fullWidth required>
            <InputLabel id="lease-intake-unit">Unit</InputLabel>
            <Select
              labelId="lease-intake-unit"
              label="Unit"
              value={form.unitId}
              onChange={(e) => setField('unitId', e.target.value)}
            >
              {units.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {unitDisplayName(u)}
                </MenuItem>
              ))}
            </Select>
            {helper('unitId') && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                {helper('unitId')}
              </Typography>
            )}
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="lease-intake-tenant">Tenant</InputLabel>
            <Select
              labelId="lease-intake-tenant"
              label="Tenant"
              value={form.tenantId}
              onChange={(e) => setField('tenantId', e.target.value)}
            >
              <MenuItem value="">
                <em>Not in ZenRentFlow yet</em>
              </MenuItem>
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {tenantDisplayName(t)}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
              {form.tenantId
                ? 'The lease is linked to this tenant. No invitation is sent.'
                : 'You can link the tenant later by editing the lease.'}
            </Typography>
          </FormControl>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Start date"
              type="date"
              value={form.startDate}
              onChange={(e) => setField('startDate', e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              helperText={helper('startDate')}
              FormHelperTextProps={{ sx: needsCheck('startDate') ? checkSx : undefined }}
            />
            <TextField
              label="End date"
              type="date"
              value={form.endDate}
              onChange={(e) => setField('endDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={helper('endDate') || 'Leave empty for a month-to-month lease.'}
              FormHelperTextProps={{ sx: needsCheck('endDate') ? checkSx : undefined }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {moneyField('monthlyRent', 'Monthly rent')}
            {moneyField('securityDeposit', 'Security deposit')}
            {moneyField('lateFee', 'Late fee')}
          </Stack>
          <FormControl fullWidth>
            <InputLabel id="lease-intake-status">Status</InputLabel>
            <Select
              labelId="lease-intake-status"
              label="Status"
              value={form.status}
              onChange={(e) => setField('status', e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending (starts later)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 2000 }}
          />
          {problems.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Before creating the lease: {problems.join(' ')}
            </Typography>
          )}
        </Stack>
      </Grid>
    </Grid>
  );

  const titles = {
    choose: 'Add a lease from the signed copy',
    uploading: 'Uploading the lease',
    reading: 'Reading the lease',
    loading: 'Opening the lease',
    review: 'Check the lease details'
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth={phase === 'review' ? 'lg' : 'sm'} fullScreen={fullScreen}>
      <DialogTitle>{titles[phase]}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              duplicate && duplicate.status !== 'confirmed' ? (
                <Button color="inherit" size="small" onClick={() => openExisting(duplicate.id)}>
                  Continue reviewing it
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        )}
        {phase === 'choose' && renderChoose()}
        {phase === 'uploading' && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Uploading… {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} aria-label="Upload progress" />
          </Box>
        )}
        {(phase === 'reading' || phase === 'loading') && (
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress aria-label="Reading the lease" />
            <Typography variant="body2" color="text.secondary">
              {phase === 'reading' && aiEnabled
                ? 'Reading the pages. This can take up to a minute.'
                : 'One moment…'}
            </Typography>
          </Stack>
        )}
        {phase === 'review' && form && renderReview()}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, p: 2 }}>
        {phase === 'review' && (
          <>
            {confirmingDiscard ? (
              <>
                <Typography variant="body2" sx={{ mr: 'auto' }}>
                  Delete these pages? No lease is created.
                </Typography>
                <Button onClick={() => setConfirmingDiscard(false)} disabled={busy}>
                  Keep
                </Button>
                <Button color="error" onClick={discard} disabled={busy}>
                  Delete pages
                </Button>
              </>
            ) : (
              <>
                <Button color="error" onClick={() => setConfirmingDiscard(true)} disabled={busy} sx={{ mr: 'auto' }}>
                  Discard
                </Button>
                {aiEnabled && (readFailed || error) && (
                  <Button onClick={() => readAndReview(intake.id)} disabled={busy}>
                    Read again
                  </Button>
                )}
                <Button onClick={onClose} disabled={busy}>
                  Finish later
                </Button>
                <Button variant="contained" onClick={createLease} disabled={busy || problems.length > 0}>
                  {busy ? 'Creating…' : 'Create lease'}
                </Button>
              </>
            )}
          </>
        )}
        {phase === 'choose' && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={upload} disabled={!propertyId || pages.length === 0}>
              {aiEnabled ? 'Upload and read' : 'Upload'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default LeaseIntakeDialog;
